import { router } from '@inertiajs/react'
import { AdminSidebar } from '@/components/layout/AdminSidebar'
import { cn } from '@/lib/utils'

interface UserRow {
  id: number; name: string; email: string
  subscription_status: string; admin: boolean
  github_connected: boolean; created_at: string
}

interface Pagination { page: number; per_page: number; total: number }

export default function Index({ users, pagination }: { users: UserRow[]; pagination: Pagination }) {
  const totalPages = Math.ceil(pagination.total / pagination.per_page)

  const grantPro = (id: number) => router.patch(`/admin/users/${id}`, { grant_pro: true })
  const revokePro = (id: number) => {
    if (confirm('Revoke Pro access?')) router.patch(`/admin/users/${id}`, { revoke_pro: true })
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar active="/admin/users" />
      <main className="flex-1 p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Users <span className="text-xl text-gray-400 font-normal">({pagination.total})</span></h1>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {['Name / Email', 'Status', 'GitHub', 'Joined', 'Actions'].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50/50">
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-900 text-sm">{u.name}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </td>
                  <td className="px-5 py-4">
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full capitalize',
                      u.subscription_status === 'pro' ? 'bg-brand/10 text-brand' :
                      u.subscription_status === 'cancelled' ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-500'
                    )}>
                      {u.admin ? 'Admin' : u.subscription_status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400">
                    {u.github_connected ? '✓' : '—'}
                  </td>
                  <td className="px-5 py-4 text-xs text-gray-400">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    {!u.admin && (
                      u.subscription_status === 'pro' ? (
                        <button onClick={() => revokePro(u.id)} className="text-red-500 hover:text-red-700 text-xs">Revoke Pro</button>
                      ) : (
                        <button onClick={() => grantPro(u.id)} className="text-brand hover:underline text-xs">Grant Pro</button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div className="py-16 text-center text-gray-400 text-sm">No users yet.</div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => router.get('/admin/users', { page: p })}
                className={cn(
                  'w-9 h-9 rounded-lg text-sm font-medium transition-colors',
                  p === pagination.page
                    ? 'bg-brand text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                )}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
